import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { foyerApi, type BulkActionResult } from "../http/foyer";

export type BulkAction = "reboot" | "message";

interface Props {
  open: boolean;
  premiseId: string;
  action: BulkAction;
  /** The hand-picked screens this action targets. */
  screenIds: string[];
  onClose: () => void;
  /** Called with the API result once the action is dispatched. */
  onDone: (result: BulkActionResult, action: BulkAction) => void;
}

/**
 * Confirm + dispatch a Fleet bulk action (reboot / message). Each action is
 * routed server-side through a rollout, so this dialog also offers staged
 * delivery: dispatch a canary slice first, then expand the rest from the
 * Rollouts page. Replaces the old `confirm()` / `prompt()` flows.
 */
export default function BulkActionDialog({
  open,
  premiseId,
  action,
  screenIds,
  onClose,
  onDone,
}: Props) {
  const count = screenIds.length;
  const [body, setBody] = useState("");
  const [stage, setStage] = useState(false);
  const [canaryPct, setCanaryPct] = useState(10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form each time the dialog (re)opens for a fresh selection.
  useEffect(() => {
    if (open) {
      setBody("");
      setStage(false);
      setCanaryPct(10);
      setError(null);
      setBusy(false);
    }
  }, [open, action]);

  const isMessage = action === "message";
  const canSubmit = !busy && count > 0 && (!isMessage || body.trim() !== "");

  async function submit() {
    setBusy(true);
    setError(null);
    const pct = stage ? Math.max(1, Math.min(100, canaryPct)) : undefined;
    try {
      const result = isMessage
        ? await foyerApi.messageBulk({
            premiseId,
            screenIds,
            body: body.trim(),
            title: "Reception",
            canaryPct: pct,
          })
        : await foyerApi.rebootBulk(premiseId, screenIds, pct);
      onDone(result, action);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  const plural = count === 1 ? "" : "s";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {isMessage
          ? `Send message to ${count} TV${plural}`
          : `Reboot ${count} TV${plural}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          {!isMessage && (
            <DialogContentText>
              This reboots the {count} selected TV{plural}. It&apos;s routed
              through a rollout, so it&apos;s tracked and reversible mid-flight.
            </DialogContentText>
          )}
          {isMessage && (
            <TextField
              label="Message"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="e.g. Breakfast is served until 10:00"
              multiline
              minRows={2}
              fullWidth
              autoFocus
              data-testid="bulk-message-body"
            />
          )}

          <FormControlLabel
            control={
              <Switch
                checked={stage}
                onChange={(e) => setStage(e.target.checked)}
                data-testid="bulk-stage-toggle"
              />
            }
            label="Stage the rollout (canary first)"
          />
          {stage && (
            <Stack spacing={1}>
              <TextField
                label="Canary %"
                type="number"
                size="small"
                value={canaryPct}
                onChange={(e) => setCanaryPct(Number(e.target.value) || 0)}
                inputProps={{
                  min: 1,
                  max: 100,
                  "data-testid": "bulk-canary-pct",
                }}
                sx={{ maxWidth: 140 }}
              />
              <DialogContentText sx={{ fontSize: 13 }}>
                Sends to the first {canaryPct}% now. Expand to the rest from the
                Rollouts page once you&apos;re happy.
              </DialogContentText>
            </Stack>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="bulk-action-confirm"
        >
          {stage ? "Start canary" : isMessage ? "Send" : "Reboot"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
